import { Capacitor } from "@capacitor/core";
import { isMobile as isReactDeviceMobile } from "react-device-detect";

/**
 * Utilidad para detectar la plataforma actual de forma centralizada
 * Usa Capacitor.getPlatform() en lugar del deprecado Capacitor.platform
 * @module platformDetector
 */

/**
 * Obtiene la plataforma actual de forma segura
 * @returns {'web' | 'ios' | 'android'} La plataforma actual
 */
export const getPlatform = () => {
  try {
    return Capacitor.getPlatform();
  } catch (error) {
    console.error("Error al detectar plataforma:", error);
    return "web"; // Fallback seguro
  }
};

/**
 * Verifica si la aplicación está corriendo en una plataforma móvil nativa
 * @returns {boolean} true si es iOS o Android nativo
 */
export const isNativeMobile = () => {
  const platform = getPlatform();
  return platform === "ios" || platform === "android";
};

/**
 * Verifica si la aplicación está corriendo en iOS nativo
 * @returns {boolean} true si es iOS nativo
 */
export const isNativeIOS = () => {
  return getPlatform() === "ios";
};

/**
 * Verifica si la aplicación está corriendo en Android nativo
 * @returns {boolean} true si es Android nativo
 */
export const isNativeAndroid = () => {
  return getPlatform() === "android";
};

/**
 * Verifica si la aplicación está corriendo en web
 * @returns {boolean} true si es web
 */
export const isWeb = () => {
  return getPlatform() === "web";
};

/**
 * Verifica si Capacitor está disponible y funcional
 * @returns {boolean} true si Capacitor está disponible
 */
export const isCapacitorAvailable = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch (error) {
    return false;
  }
};

/**
 * Obtiene información detallada de la plataforma
 * @returns {Object} Objeto con información de la plataforma
 */
export const getPlatformInfo = () => {
  const platform = getPlatform();
  return {
    platform,
    isNative: isNativeMobile(),
    isIOS: isNativeIOS(),
    isAndroid: isNativeAndroid(),
    isWeb: isWeb(),
    isCapacitorAvailable: isCapacitorAvailable(),
  };
};

/**
 * Breakpoints de la aplicación
 */
export const BREAKPOINTS = {
  MOBILE_MAX: 1024,
  DESKTOP_MIN: 1280,
};

/**
 * Verifica si el ancho de la ventana es de mobile/tablet
 * @returns {boolean} true si width <= 1024px
 */
export const isMobileWidth = () => {
  return window.innerWidth <= BREAKPOINTS.MOBILE_MAX;
};

/**
 * Verifica si el ancho de la ventana es de desktop
 * @returns {boolean} true si width >= 1280px
 */
export const isDesktopWidth = () => {
  return window.innerWidth >= BREAKPOINTS.DESKTOP_MIN;
};

/**
 * Detecta si el dispositivo es móvil o tablet (web o app nativa)
 * Usa User Agent para la detección
 * @returns {boolean} true si es móvil o tablet
 */
export const isMobile = () => {
  return isReactDeviceMobile;
};

/**
 * Hook de React personalizado para usar la detección de plataforma
 * @returns {Object} Objeto con información de la plataforma
 */
export const usePlatform = () => {
  return getPlatformInfo();
};
